import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
  searchThankyouCallTelRequestList,
  searchThankyouCallLpRequestList,
  searchThankyouCallGuideList,
  saveThankyouCallLpRequestList,
} from '@store/actions/thankyouCallListAction';

import { format } from 'date-fns';

import SatInput from '@components/common/satUI/SatInput';
import SatModal from '@components/common/satUI/SatModal';
import SatButton from '@components/common/satUI/SatButton';

import ThankyouCallTelList from '@components/appList/ThankyouCallTelList';
import ThankyouCallLpRequestList from '@components/appList/ThankyouCallLpRequestList';
import ThankyouCallGuideList from '@components/appList/ThankyouCallGuideList';
import ThankyouCallLpRequest from '@components/appList/ThankyouCallLpRequest';

import { AVAILABLE_TIME_LIST } from '@common/constants';
import { showAlert } from '@store/actions/commonAction';

const stateSelector = createSelector(
  state => state.thankyouCallListReducer,
  thankyouCallListReducer => ({
    thankyouCallTelRequestList:
      thankyouCallListReducer?.thankyouCallTelRequestList ?? {},
    thankyouCallLpRequestList:
      thankyouCallListReducer?.thankyouCallLpRequestList ?? {},
    thankyouCallGuideList: thankyouCallListReducer?.thankyouCallGuideList ?? {},
  }),
);

const LAST_TIME = '1800';

const ThankyouCallDetailPopup = props => {
  const {
    policyNo,
    ownerName,
    procTypeCode,
    procTypeName,
    thankyouCallId,
    isDisabledLpRequest = false, // 땡큐콜 요청 불가 여부
    imgScanYN = 'Y',
    scriptContent = '',
    reDescObjYN, // 모바일 재설명 대상여부(Y/N)
    reDescYN, // 재설명 여부 (Y/N)
    reMonitorDateTime, // 모바일 재모니터링 가능 일시
    onClosePopup,
    phoneNumber,
    reMonitorObjYN,
    isCompany,
    callTarget,
    mobileDispYN,
  } = props;

  const {
    thankyouCallTelRequestList,
    thankyouCallLpRequestList,
    thankyouCallGuideList,
  } = useSelector(stateSelector);

  const [lpRequestInputData, setLpRequestInputData] = useState({
    availableDate: '',
    availableStartTime: '',
    availableEndTime: '',
    isHoldRequest: '',
    requestText: '',
    isRetellingCompleted: '',
  });
  //재설명 채크 했을때 lpRequestInputData를 임시로 들고 있는 부분
  const [tempLpRequestInputData, setTempLpRequestInputData] = useState();
  const [availableStartTimeList, setAvailableStartTimeList] =
    useState(AVAILABLE_TIME_LIST);
  const [availableEndTimeList, setAvailableEndTimeList] =
    useState(AVAILABLE_TIME_LIST);

  const [isOpenNotReservationPopup, setIsOpenNotReservationPopup] =
    useState(isDisabledLpRequest);

  const [isShowReMonitoringAlamTalk, setIsShowReMonitoringAlamTalk] =
    useState(false);

  // 재 설명 완료 저장 메시지 팝업
  const [
    isOpenReDescriptionCompletedPopup,
    setIsOpenReDescriptionCompletedPopup,
  ] = useState(false);

  //재설명 여부
  const [reDescriptYN, setReDescriptYN] = useState(reDescYN);
  //재 모니터링 가능 일시
  const [reMonitorDateTimeState, setReMonitorDateTimeState] =
    useState(reMonitorDateTime);

  // to do 휴일에 일시 선택으로
  const [currentDate] = useState(format(new Date(), 'yyyyMMdd'));
  const [currentHour] = useState(parseInt(format(new Date(), 'HH')));
  const [currentMinutes] = useState(parseInt(format(new Date(), 'mm')));

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      searchThankyouCallTelRequestList({ thankyouCallId: thankyouCallId }),
    );
    dispatch(
      searchThankyouCallLpRequestList({ thankyouCallId: thankyouCallId }),
    );
    dispatch(searchThankyouCallGuideList({ thankyouCallId: thankyouCallId })); // test callId =>'21411965'

    initLpRequestInputData();
  }, []);

  // 요청사항 저장
  const handleSaveRequest = input => {
    if (input.isRetellingCompleted) setIsShowReMonitoringAlamTalk(true);
    // 재설명 완료 요청
    else saveRequest(input);
  };

  const saveRequest = input => {
    const cb = (status, data) => {
      if (status && input.isRetellingCompleted) {
        const ThankyoucallDetail = data.find(
          detail => detail.thankyouCallId === thankyouCallId,
        );

        if (ThankyoucallDetail?.reDescYN !== 'Y') return;

        // 재설명 완료 요청은 한번만 요구사항
        setReDescriptYN(ThankyoucallDetail?.reDescYN); // reset
        setReMonitorDateTimeState(ThankyoucallDetail?.reMonitorDateTime); // reset
        setLpRequestInputData({
          ...lpRequestInputData,
          isRetellingCompleted: false,
          requestText: '',
        });
      }
    };
    const requestParams = {
      thankyouCallId: thankyouCallId,
      comment: input.requestText,
      reqStartDateTime:
        input.isHoldRequest || input.isRetellingCompleted
          ? ''
          : input.availableDate.concat(input.availableStartTime),
      reqEndDateTime:
        input.isHoldRequest || input.isRetellingCompleted
          ? ''
          : input.availableDate.concat(input.availableEndTime),
      reDescYN: input.isRetellingCompleted ? 'Y' : '',
    };

    // console.log('requestParams' + requestParams);
    dispatch(saveThankyouCallLpRequestList({ ...requestParams, callback: cb })); // to do
  };

  // 초기 연락가능 일시 셋팅
  const initLpRequestInputData = () => {
    const availableStartTime = getAvailableStartTime(
      currentHour,
      currentMinutes,
    );

    const availableEndTime = getAvailableEndTime(availableStartTime);

    // 요청 사항 input
    setLpRequestInputData({
      ...lpRequestInputData,
      availableDate: currentDate,
      availableStartTime: availableStartTime,
      availableEndTime: availableEndTime,
    });

    // 시간 선택 옵션
    const startTimeList = getAvailableTimeListDisabled(
      AVAILABLE_TIME_LIST,
      availableStartTime,
      true,
    );
    setAvailableStartTimeList(startTimeList);

    const endTimeList = getAvailableTimeListDisabled(
      AVAILABLE_TIME_LIST,
      availableEndTime,
      true,
    );
    setAvailableEndTimeList(endTimeList);
  };

  // 시작시간 셋팅
  const getAvailableStartTime = (currentHour, currentMinutes) => {
    let availableHour = 0;
    let availableMinute = 0;

    if (currentHour >= 18) {
      return '';
    } else if (currentHour === 17 && currentMinutes >= 30) {
      return LAST_TIME;
    }

    if (currentHour < 9) {
      availableHour = 9;
      availableMinute = 0;
    } else {
      if (currentMinutes >= 30) {
        availableHour = currentHour + 1;
      } else {
        availableHour = currentHour;
        availableMinute = 30;
      }
    }
    return getTimeString(availableHour, availableMinute);
  };

  // 종료시간 셋팅
  const getAvailableEndTime = startTime => {
    let availableHour = parseInt(startTime.substring(0, 2));
    let availableMinute = parseInt(startTime.substring(2));

    if (startTime === '') {
      return '';
    } else if (startTime === LAST_TIME) {
      return startTime;
    }

    if (availableMinute >= 30) {
      availableHour = availableHour + 1;
      availableMinute = 0;
    } else {
      availableMinute = 30;
    }
    return getTimeString(availableHour, availableMinute);
  };

  // 시간 선택 옵션 리스트 셋팅
  const getAvailableTimeListDisabled = (list, time, isToday) => {
    if (time === '') {
      return list.map(obj => ({ ...obj, disabled: true }));
    }
    const availableTimeList = list.map(obj => {
      return obj.value < time && isToday ? { ...obj, disabled: true } : obj;
    });

    return availableTimeList;
  };

  // convert int time to string
  const getTimeString = (hour, minute) => {
    return hour
      .toString()
      .padStart(2, '0')
      .concat(minute.toString().padStart(2, '0'));
  };

  // 요청사항 입력 값 수정
  const handleChangeLpRequestInputData = (type, value) => {
    if (type === 'availableStartTime' || type === 'availableEndTime') {
      if (
        imgScanYN === 'Y' &&
        ['811', '821', '822', '818', '812', '831'].includes(procTypeCode)
      ) {
        dispatch(
          showAlert({
            message:
              '모바일 모니터링기능 건으로 본인인증(휴대폰, KB국민인증서, 신용카드)후\n 빠르고 편하게 모니터링가능합니다.\n모바일 이용이 가능하지 않은 경우에만 통화가능 시간예약을 진행해 주시기 바랍니다.',
            wide: true,
          }),
        );
      }

      if (
        !lpRequestInputData.isRetellingCompleted &&
        reDescObjYN !== 'N' &&
        !isCompany
      ) {
        if (type === 'availableStartTime') {
          const endTime = getAvailableEndTime(value);
          const endTimeList = getAvailableTimeListDisabled(
            AVAILABLE_TIME_LIST,
            endTime,
            true,
          );
          setAvailableEndTimeList(endTimeList);
          setLpRequestInputData({
            ...lpRequestInputData,
            availableStartTime: value,
            availableEndTime: endTime,
          });
        } else {
          setLpRequestInputData({ ...lpRequestInputData, [type]: value });
        }
        setIsOpenReDescriptionCompletedPopup(true);
        return;
      }
    }
    if (type === 'availableDate') {
      // 날짜 변경시
      if (currentDate === value) {
        // 오늘 날짜 일때
        const availableStartTime = getAvailableStartTime(
          currentHour,
          currentMinutes,
        );
        const startTimeList = getAvailableTimeListDisabled(
          AVAILABLE_TIME_LIST,
          availableStartTime,
          true,
        );
        setAvailableStartTimeList(startTimeList);

        const availableEndTime = getAvailableEndTime(availableStartTime);
        const endTimeList = getAvailableTimeListDisabled(
          AVAILABLE_TIME_LIST,
          availableEndTime,
          true,
        );
        setAvailableEndTimeList(endTimeList);
        setLpRequestInputData({
          ...lpRequestInputData,
          availableDate: value,
          availableStartTime: availableStartTime,
          availableEndTime: availableEndTime,
        });
      } else {
        // 오늘 날짜가 아닐 때
        setAvailableStartTimeList(AVAILABLE_TIME_LIST);
        setAvailableEndTimeList(AVAILABLE_TIME_LIST);
        setLpRequestInputData({
          ...lpRequestInputData,
          availableDate: value,
          availableStartTime: '',
          availableEndTime: '',
        });
      }
      return;
    } else if (type === 'availableStartTime') {
      const endTime = getAvailableEndTime(value);
      const endTimeList = getAvailableTimeListDisabled(
        AVAILABLE_TIME_LIST,
        endTime,
        true,
      );
      setAvailableEndTimeList(endTimeList);
      setLpRequestInputData({
        ...lpRequestInputData,
        availableStartTime: value,
        availableEndTime: endTime,
      });
      return;
    }

    if (type === 'isHoldRequest') {
      setLpRequestInputData({
        ...lpRequestInputData,
        isHoldRequest: value,
        requestText: value ? '보류신청' : '',
      });
      return;
    }

    if (type === 'isRetellingCompleted') {
      // 재설명 여부
      setLpRequestInputData({
        ...lpRequestInputData,
        isRetellingCompleted: value,
        requestText: value ? '모바일 재모니터링 대상_재설명 완료' : '',
      });
      return;
    }
    setLpRequestInputData({ ...lpRequestInputData, [type]: value });
  };

  const ThankyouCallStatusLabelRender = ({ procTypeCode, procTypeName }) => {
    switch (procTypeCode) {
      case '841': // 정상
      case '842': // 무효
      case '843': // 3대기본미이행
      case '844': // 30일 경과
      case '845': // 적합성미이행
      case '846': // 자금세탁방지업무상이
      case '847': // 추가고지
      case '816': // 계약자완료
      case '817': // 피보험자완료
        return <span className="status-label complete mgl-5">완료</span>;
      case '811': // 진행중
      case '821': // 대기
      case '822': // 분배
      case '818': // 친권자진행중
        return <span className="status-label under-review mgl-5">진행중</span>;
      case '814': // 보완(철회예정)
      case '833': // 철회예정
        return <span className="status-label return mgl-5">철회예정</span>;
      case '813': // 보완(필수미안내)
      case '815': // 보완(상품적합성미안내)
      case '832': // 필수사항미안내
      case '834': // 상품적합성미안내
        return (
          <span className="status-label return mgl-5">필수사항미안내</span>
        );
      case '812': // 보완(연락불가)
      case '831': // 연락불가
        return <span className="status-label return mgl-5">연락불가</span>;
      default:
        return (
          <span className="status-label under-review mgl-5">
            {procTypeName}
          </span>
        );
    }
  };

  return (
    <>
      {/* <!-- con-wrap: con-bottom이 없는 popup은 no-bottom 추가 --> */}
      <header className="con-header">
        <div className="page-navi">
          <span onClick={onClosePopup} className="btn-back">
            이전
          </span>
        </div>
        <h1 className="page-tit">{'완전판매모니터링 (해피콜)'}</h1>
      </header>
      <div className="con-wrap no-bottom">
        <div className="pop-box2">
          <div className="tit-area">
            <h2 className="tit-con">완전판매모니터링(해피콜) 상태</h2>

            {/* <span className="status-label under-review mgl-5">
              {procTypeName}
            </span> */}
            <ThankyouCallStatusLabelRender
              procTypeCode={procTypeCode}
              procTypeName={procTypeName}
            />
          </div>
          <div className="box two">
            <SatInput value={policyNo} label="증권번호" type="text" readOnly />

            <SatInput value={ownerName} label="계약자" type="text" readOnly />
          </div>

          {/* 통화이력 */}
          <ThankyouCallTelList
            thankyouCallTelList={thankyouCallTelRequestList?.list}
          />

          {/* 필수사항 미안내 내용 */}
          <ThankyouCallGuideList
            thankyouCallGuideList={thankyouCallGuideList?.list}
            scriptContent={scriptContent}
          />

          {/* 예약 등록 */}
          <ThankyouCallLpRequest
            lpRequestInputData={lpRequestInputData}
            availableStartTimeList={availableStartTimeList}
            availableEndTimeList={availableEndTimeList}
            isDisabledLpRequest={isDisabledLpRequest}
            imgScanYN={imgScanYN}
            reDescObjYN={reDescObjYN}
            reDescYN={reDescriptYN}
            reMonitorObjYN={reMonitorObjYN}
            reMonitorDateTime={reMonitorDateTimeState}
            onChangeInputData={handleChangeLpRequestInputData}
            onClickSave={handleSaveRequest}
            isCompany={isCompany}
            callTarget={callTarget}
          />

          {/* 예약이력 */}
          <ThankyouCallLpRequestList
            thankyouCallLpRequestList={thankyouCallLpRequestList?.list}
          />
          <SatModal
            visible={isOpenNotReservationPopup}
            setVisible={() => {
              setIsOpenNotReservationPopup(false);
            }}
            type="info"
            msg="통화시간 예약이 가능하지 않습니다."
            onOk={{
              handle: () => {
                setIsOpenNotReservationPopup(false);
              },
            }}
          />
          <SatModal
            visible={isOpenReDescriptionCompletedPopup}
            setVisible={() => {
              setIsOpenReDescriptionCompletedPopup(false);
            }}
            type="confirm"
            hideCloseButton={true}
            render={() => {
              return (
                <>
                  <h3 className="layer-tit">알림</h3>
                  <div className="cont">
                    <p className="layer-text">
                      [모바일 재모니터링대상 건]입니다.
                      <br />
                      재설명을 완료하셨다면 모바일을 통해 고객님이 직접
                      재모니터링 진행하시도록 신청가능합니다.
                      <br />
                      확인 선택시 모바일재모니터링 가능한 알림톡이 고객님께
                      자동발송됩니다.
                      <br />
                      *모바일재모니터링이 불가한 경우만 취소 선택 후 연락일시를
                      등록해 주시기 바랍니다.
                    </p>
                  </div>
                  <div className="btn-group">
                    <SatButton
                      styleClass="btn-half cancel"
                      label={'취소'}
                      onClick={() =>
                        setIsOpenReDescriptionCompletedPopup(false)
                      }
                    />
                    <SatButton
                      styleClass="btn-half confirm"
                      label={'확인'}
                      onClick={() => {
                        handleChangeLpRequestInputData(
                          'isRetellingCompleted',
                          true,
                        );
                        setIsOpenReDescriptionCompletedPopup(false);
                      }}
                    />
                  </div>
                </>
              );
            }}
          />
          {/*재모니터링 알림톡*/}
          <SatModal
            type="confirm"
            visible={isShowReMonitoringAlamTalk}
            setVisible={() => setIsShowReMonitoringAlamTalk(false)}
          >
            <div className="cont">
              <h3 className="layer-tit">알림톡 발송</h3>
              <p className="layer-text">
                고객님의 휴대전화번호 ({phoneNumber})로
                <br />
                모바일재모니터링알림톡을 재발송하시겠습니까?
              </p>
            </div>
            <div className="btn-group">
              <SatButton
                styleClass="btn-half cancel modal-close"
                label={'취소'}
                onClick={() => setIsShowReMonitoringAlamTalk(false)}
              />
              <SatButton
                styleClass="btn-half confirm modal-close"
                label={'확인'}
                onClick={() => {
                  saveRequest(lpRequestInputData);
                  setIsShowReMonitoringAlamTalk(false);
                }}
              />
            </div>
          </SatModal>
        </div>
      </div>
    </>
  );
};

export default ThankyouCallDetailPopup;
